<template>
  <div
    class="bg-black-transparent absolute w-full h-full backdrop-filter backdrop-blur-sm top-0 left-0 flex items-center justify-center">
    <div id="generate_pdf_popup" :class="{'h-17/20': !pdf, 'h-8/10': pdf}"
         class="bg-white classen__rounded px-5 pt-5 pb-10 flex flex-wrap">
      <div class="w-full h-content">
        <p class="text-3xl text-right w-full cursor-pointer" @click="$emit('onClose')"><i class="fas fa-times"></i>
        </p>
        <p class="title text-3xl text-center w-full">{{ getTranslation('generatePdf') }}</p>
      </div>
      <div v-if="!pdf" class="w-4/5 ml-20">
        <p class="text-base text-left w-full">
          {{ getTranslation('currentSelection') }}: {{ selection.name }}
        </p>
        <p class="title text-base capitalize mt-4 flex justify-start">
          {{ getTranslation('information') }}:</p>
        <div class="flex mt-2" @click="selectAllOptions()">
          <c-checkbox :selected="selectAll" :mode="'medium'"/>
          <label>{{ getTranslation('selectAll') }}</label>
        </div>
        <div class="grid grid-cols-2 mb-10 mt-2 ml-2">
          <template v-for="(value, propertyName) in informationOptions">
            <div @click="setProperty(value, propertyName)"
                 class="flex cursor-pointer items-center border-b py-1 border-classen__secondary"
                 :class="{'cursor-not-allowed': isRequired(propertyName)}">
              <c-checkbox :selected="value"/>
              <p>
                {{ getTranslation(propertyName) }}
                <label v-if="isRequired(propertyName)" class="text-red-600">*</label>
              </p>
            </div>
          </template>
        </div>

        <p class="title mb-1 flex justify-start">
          {{ getTranslation('sortedBy') }}:
          <label class="text-red-600">*</label>
        </p>
        <div class="flex justify-start">
          <c-option-dropdown :options="sortingMethods"
                             :selected-option.sync="selectedSort"/>
        </div>
        <p class="title mb-1 mt-10 flex justify-start">
          {{ getTranslation('format') }}:
          <label class="text-red-600">*</label>
        </p>
        <div class="flex justify-between w-full">
          <c-option-dropdown :disabled="formatDisabled" :options="formats"
                             :selected-option.sync="selectedFormat"/>
          <img class="preview" v-if="selectedFormat.value === '1'"
               src="../../../../../assets/icons/one.svg"/>
          <img class="preview" v-if="selectedFormat.value === '9'"
               src="../../../../../assets/icons/nine.svg"/>
        </div>

        <div class="w-full flex justify-center self-end mt-2">
          <button class="classen__button hover__button" @click="sendRequest">
            {{ getTranslation('generatePdf') }}
          </button>
        </div>
      </div>

      <div v-if="pdf" class="w-full text-center">
        <a :href="pdf" target="_blank" class="underline">{{ getTranslation('clickHereToOpenPdf') }}</a>
      </div>
    </div>
    <c-loader v-if="pdfGenerating"/>
  </div>
</template>

<script>
import getTranslation from "../../../../../mixins/getTranslation";
import CustomCheckbox from "../../../../form/CustomCheckbox";
import OptionDropdown from "../../../OptionDropdown";
import download from "../../../../../mixins/download";
import Loader from "../../../../Loader";

export default {
  name: "GeneratePdfPopup",

  mixins: [getTranslation, download],

  components: {
    'c-checkbox': CustomCheckbox,
    'c-option-dropdown': OptionDropdown,
    'c-loader': Loader,
  },

  props: {
    selection: {required: true},
    selectedOptions: {required: false, default: null}
  },

  data() {
    return {
      formatDisabled: false,
      pdfGenerating: false,
      informationOptions: null,
      sortingMethods: null,
      selectedSort: {value: 'external_id', name: 'New to old', optionsName: 'articleNumber'},
      formats: [
        {value: '1', name: '1 Article per page'},
        {value: '9', name: '9 Articles per page'},
      ],
      selectedFormat: {value: '1', name: '1 Article per page'},
      selectAll: false,
      interval: null,
      pdf: null,
    }
  },

  created() {
    this.setInformationOptions();
    this.setSortingMethods();
    this.formats[0].name = this.getTranslation('1ArticlePerPage');
    this.selectedFormat.name = this.getTranslation('1ArticlePerPage');
    this.formats[1].name = this.getTranslation('9ArticlesPerPage');
    this.selectedSort.name = this.getTranslation('newToOld');

    this.checkProductCount();
  },

  destroyed() {
    clearInterval(this.interval);
  },

  methods: {
    setInformationOptions() {
      let informationOptions = {
        articleName: true,
        dimensions: false,
        articleNumber: true,
        structure: false,
        range: false,
        lockingSystem: false,
        category: false,
        bevel: false,
        collection: false,
        acClassification: false,
        productGroup: false,
        wearClass: false,
      }

      if (this.selectedOptions) {
        informationOptions = Object.keys(informationOptions)
          .filter((key) => this.selectedOptions.includes(key))
          .reduce((obj, key) => {
            obj[key] = informationOptions[key];
            return obj;
          }, {});
      }

      this.informationOptions = informationOptions;
    },

    selectAllOptions() {
      this.informationOptions = Object.keys(this.informationOptions)
        .reduce((obj, key) => {
          obj[key] = this.informationOptions[key];
          if (!this.isRequired(key)) {
            obj[key] = !this.selectAll;
          }
          return obj;
        }, {});
      this.selectAll = !Object.keys(this.informationOptions).some((option) => this.informationOptions[option] === false);
    },

    setSortingMethods() {
      let sortingMethods = [
        {value: 'external_id', name: this.getTranslation('newToOld'), optionsName: 'articleNumber'},
        {value: 'name', name: this.getTranslation('name'), optionsName: 'articleName'},
        {
          value: 'external_id',
          name: this.getTranslation('articleNumber'),
          optionsName: 'articleNumber'
        },
        {value: 'tag_id', name: this.getTranslation('range'), optionsName: 'range'},
        {value: 'category_id', name: this.getTranslation('category'), optionsName: 'category'},
      ]

      if (this.selectedOptions) {
        sortingMethods = sortingMethods.filter((method) => this.selectedOptions.includes(method.optionsName));
      }

      this.sortingMethods = sortingMethods;
    },

    checkProductCount() {
      let products = this.selection.selectionList.data.filter((product) => {
        return this.getType(product.type) === 'Product'
      })

      if (products.length >= 15) {
        this.selectedFormat = {value: '9', name: this.getTranslation('9ArticlesPerPage')};
        this.formatDisabled = true;
      }
    },

    getType(type) {
      return type.split('\\')[2]
    },

    isRequired(name) {
      return name === 'articleName' || name === 'articleNumber';
    },

    setProperty(value, propertyName) {
      if (!this.isRequired(propertyName)) {
        this.$set(this.informationOptions, propertyName, !value)
      }
      this.selectAll = !Object.keys(this.informationOptions).some((option) => this.informationOptions[option] === false);
    },

    sendRequest() {
      let selectionData = {
        data: {
          options: this.informationOptions,
          format: this.selectedFormat.value,
          sort: this.selectedSort.value,
        },
        selectionId: this.selection.id
      }
      this.pdfGenerating = true
      this.$dashboardStore.dispatch('generateSelectionPdf', selectionData)
        .then((res) => {
          let statusId = res.statusId
          this.interval = setInterval(() => {
            let selectionData = {
              selectionId: this.selection.id,
              statusId: statusId
            };

            this.$dashboardStore.dispatch('getGenerateSelectionPdfStatus', selectionData).then((status) => {
              if (status.status === 'finished') {
                clearInterval(this.interval);
                this.pdf = status.data.url;
                this.isLoaderVisible = false;
                this.pdfGenerating = false;
              }

              if (status.status === 'failed') {
                clearInterval(this.interval);
                console.error('Something went wrong');
                this.isLoaderVisible = false;
                this.pdfGenerating = false;
              }
            })
          }, 5000)
        })
    }
  }
}
</script>
<style scoped>
#generate_pdf_popup {
  width: 996px;
}

.preview {
  height: 165px;
}
</style>
